import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { assetUrl, handleImgError } from '../utils/imageHelpers';
import {
  DEFAULT_BUSINESS_HOURS,
  loadBusinessHours,
  fetchSlotAvailability,
  toMinutes,
  mexicoNowMinutes,
  mexicoTodayString,
  formatHourLabel,
} from '../utils/businessHours';

const SLOT_STEP_MINUTES = 15;

const SelectorClock = ({ onTimeChange, selectedHour, selectedMinute, deliveryType, menuDate }) => {
  const [timeOptions, setTimeOptions] = useState([]);
  const [businessHours, setBusinessHours] = useState(DEFAULT_BUSINESS_HOURS);
  const [slotMode, setSlotMode] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  // El menú se renderiza en un portal con posición fija para que no quede
  // atrapado por el overflow/transform del modal de entrega.
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const [menuPosition, setMenuPosition] = useState(null);

  const MENU_MAX_HEIGHT = 192; // max-h-48

  const toggleMenu = () => {
    if (isOpen) {
      setIsOpen(false);
      return;
    }
    const rect = buttonRef.current?.getBoundingClientRect();
    if (!rect) return;
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUpward = spaceBelow < MENU_MAX_HEIGHT + 24 && rect.top > MENU_MAX_HEIGHT + 24;
    setMenuPosition({
      left: rect.left,
      width: rect.width,
      ...(openUpward
        ? { bottom: window.innerHeight - rect.top + 8 }
        : { top: rect.bottom + 8 }),
    });
    setIsOpen(true);
  };

  // Cerrar si el usuario scrollea o cambia el tamaño (la posición fija quedaría desfasada)
  useEffect(() => {
    if (!isOpen) return;
    const close = (event) => {
      // Scrollear dentro del propio menú no debe cerrarlo
      if (menuRef.current && event.target && menuRef.current.contains(event.target)) return;
      setIsOpen(false);
    };
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [isOpen]);

  // 'delivery' y 'someone_else' consumen cupo de domicilio; 'pickup' el de tienda.
  const isHomeDelivery = deliveryType === 'delivery' || deliveryType === 'someone_else';
  const targetDate = menuDate || mexicoTodayString();

  useEffect(() => {
    let active = true;
    const load = async () => {
      const [hours, availability] = await Promise.all([
        loadBusinessHours(),
        fetchSlotAvailability(targetDate),
      ]);
      if (!active) return;
      setBusinessHours(hours);
      if (availability.configured) {
        setSlotMode(true);
        const options = buildSlotOptions(availability.slots);
        setTimeOptions(options.length > 0 ? options : buildAsapOptions(hours));
      } else {
        setSlotMode(false);
        setTimeOptions(buildLegacyOptions(hours));
      }
    };
    load();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deliveryType, targetDate]);

  // Eliminada la selección automática de la primera hora disponible. El usuario debe seleccionar manualmente.

  // Oculta horas que ya pasaron cuando el pedido es para hoy (hora de México).
  const isPastSlot = (slotMin) =>
    targetDate === mexicoTodayString() && slotMin <= mexicoNowMinutes();

  // Modo slots: horarios definidos por el negocio, con cupo por tipo de entrega.
  const buildSlotOptions = (slots) => {
    return slots
      .map((slot) => {
        const limit = isHomeDelivery ? slot.delivery_limit : slot.pickup_limit;
        const remaining = isHomeDelivery ? slot.delivery_remaining : slot.pickup_remaining;
        const slotMin = toMinutes(slot.slot_time);
        const hour = Math.floor(slotMin / 60);
        const minute = slotMin % 60;
        return {
          hour,
          minute,
          display: formatDisplayTime(hour, minute),
          offered: limit > 0,
          full: limit > 0 && remaining <= 0,
          available: limit > 0 && remaining > 0 && !isPastSlot(slotMin),
          visible: limit > 0 && !isPastSlot(slotMin),
          value: `${hour}:${minute.toString().padStart(2, '0')}`
        };
      })
      .filter((option) => option.visible);
  };

  // Cliente tardío: ya pasaron todas las horas de entrega de hoy pero la venta
  // del día sigue abierta (orderEnd). La tienda recibe sin cupo, así que se
  // ofrece la hora actual; a domicilio no, esa ruta ya cerró.
  const buildAsapOptions = (hours) => {
    const nowMin = mexicoNowMinutes();
    const saleStillOpen = targetDate === mexicoTodayString() && nowMin < toMinutes(hours.orderEnd);
    if (isHomeDelivery || !saleStillOpen) return [];

    const hour = Math.floor(nowMin / 60);
    const minute = nowMin % 60;
    return [{
      hour,
      minute,
      display: 'Lo antes posible',
      full: false,
      available: true,
      visible: true,
      value: `${hour}:${minute.toString().padStart(2, '0')}`
    }];
  };

  // Modo legacy (sin slots configurados): cada 15 min dentro de la ventana de venta.
  const buildLegacyOptions = (hours) => {
    const options = [];
    const startMin = toMinutes(hours.orderStart);
    const endMin = toMinutes(hours.orderEnd);
    const nowMin = mexicoNowMinutes();

    // Dentro de la ventana de venta solo se muestran horarios futuros;
    // fuera de ella el pedido es para mañana y se muestran todos.
    const showAllSlots = !(nowMin >= startMin && nowMin < endMin);

    for (let slot = startMin; slot <= endMin; slot += SLOT_STEP_MINUTES) {
      const hour = Math.floor(slot / 60);
      const minute = slot % 60;
      const isAvailable = showAllSlots || slot > nowMin;
      options.push({
        hour,
        minute,
        display: formatDisplayTime(hour, minute),
        full: false,
        available: isAvailable,
        visible: isAvailable,
        value: `${hour}:${minute.toString().padStart(2, '0')}`
      });
    }
    return options;
  };

  const formatDisplayTime = (hour, minute) => {
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
    return `${displayHour}:${minute.toString().padStart(2, '0')} ${ampm}`;
  };

  const handleTimeSelect = (option) => {
    if (option.available) {
      onTimeChange(option.hour.toString(), option.minute.toString());
      setIsOpen(false);
    }
  };

  const getCurrentSelection = () => {
  if (!selectedHour || !selectedMinute) return 'Seleccionar hora de entrega';
    
    const hour = parseInt(selectedHour, 10);
    const minute = parseInt(selectedMinute, 10);
    return formatDisplayTime(hour, minute);
  };

  const isSelectedTimeAvailable = () => {
    if (!selectedHour || !selectedMinute) return true;
    
    const selectedOption = timeOptions.find(
      option => option.hour === parseInt(selectedHour, 10) && 
                option.minute === parseInt(selectedMinute, 10)
    );
    
    return selectedOption?.available ?? false;
  };

  return (
    <div className="relative">
      {/* Dropdown button */}
      <button
        ref={buttonRef}
        type="button"
        onClick={toggleMenu}
        className={`w-full p-3 text-sm rounded-xl bg-white/10 backdrop-blur-sm border transition-all duration-200 flex items-center justify-between ${
          isSelectedTimeAvailable() 
            ? 'text-white border-white/20 hover:border-white/40 focus:border-white/40' 
            : 'text-red-300 border-red-400/50'
        } focus:outline-none focus:ring-2 focus:ring-white/20`}
      >
        <span className={selectedHour && selectedMinute ? 'text-white' : 'text-white/50'}>
          {getCurrentSelection()}
        </span>
        <img 
          src={assetUrl('/assets/clock.svg')} 
          alt="Reloj" 
          className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} 
          onError={handleImgError} 
        />
      </button>

      {/* Time warning if selected time is not available */}
      {selectedHour && selectedMinute && !isSelectedTimeAvailable() && (
        <div className="mt-2 p-2 bg-red-500/20 border border-red-400/30 rounded-lg">
          <p className="text-red-300 text-xs text-center">
            Esta hora ya no está disponible. Por favor selecciona otra.
          </p>
        </div>
      )}

      {/* Dropdown menu (portal): flota encima del modal, no queda atrapado por su overflow */}
      {isOpen && menuPosition && createPortal(
        <div
          ref={menuRef}
          className="fixed z-[10000] bg-white/95 backdrop-blur-sm rounded-xl border border-white/20 shadow-2xl max-h-48 overflow-y-auto custom-scrollbar"
          style={menuPosition}
        >
          <div className="p-2">
            {timeOptions.filter(option => option.visible).length === 0 && (
              <p className="p-3 text-sm text-gray-500 text-center">
                No hay horarios disponibles para esta fecha.
              </p>
            )}
            {timeOptions.filter(option => option.visible).map((option, index) => (
              <button
                key={index}
                type="button"
                onClick={() => handleTimeSelect(option)}
                disabled={!option.available}
                className={`w-full p-3 text-left text-sm rounded-lg transition-all duration-200 flex items-center justify-between ${
                  !option.available
                    ? 'text-gray-400 cursor-not-allowed'
                    : selectedHour === option.hour.toString() && selectedMinute === option.minute.toString()
                    ? 'bg-red-200 text-red-800 font-medium cursor-pointer'
                    : 'hover:bg-red-100 text-gray-800 cursor-pointer'
                }`}
              >
                <span className={!option.available ? 'line-through' : ''}>{option.display}</span>
                {option.full && (
                  <span className="text-xs font-medium text-red-500 bg-red-50 px-2 py-0.5 rounded-full">
                    Lleno
                  </span>
                )}
                {option.available && selectedHour === option.hour.toString() && selectedMinute === option.minute.toString() && (
                  <img
                    src={assetUrl('/assets/check-circle.svg')}
                    alt="Seleccionado"
                    className="w-4 h-4 text-green-600"
                    onError={handleImgError}
                  />
                )}
              </button>
            ))}
          </div>
        </div>,
        document.body
      )}

      {/* Business hours info */}
      <div className="mt-2 text-xs text-white/60 text-center">
        {slotMode
          ? 'Elige uno de los horarios de entrega disponibles'
          : `Horario disponible: ${formatHourLabel(businessHours.orderStart)} - ${formatHourLabel(businessHours.orderEnd)}`}
      </div>

      {/* Click outside to close (portal, debajo del menú) */}
      {isOpen && createPortal(
        <div
          className="fixed inset-0 z-[9999]"
          onClick={() => setIsOpen(false)}
        />,
        document.body
      )}
    </div>
  );
};

export default SelectorClock;
